import React, { useMemo } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

type FileDoc = {
  name?: string;
  fileName?: string;
  url?: string;
  path?: string;
  src?: string;
  link?: string;
  key?: string;
  file?: any;
  document?: any;
  value?: any;
  _id?: string;
};

type DocumentsProps = {
  data?: {
    personal?: Record<string, FileDoc | null>;
    academic?: Record<string, FileDoc | null>;
    test?: Record<string, FileDoc | null> | FileDoc[];
    workExperiences?: FileDoc[];
  };
};

const labelize = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const API_ORIGIN = "https://campusroot.com";

const isLikelyUrlOrPath = (value: string) => {
  const v = value.trim();
  return (
    /^https?:\/\//i.test(v) ||
    /^\/(?!\/)/.test(v) ||
    /^api\//i.test(v) ||
    /^uploads?\//i.test(v) ||
    /^blob:/i.test(v)
  );
};

const toAbsoluteUrl = (value: string) => {
  if (/^https?:\/\//i.test(value) || /^blob:/i.test(value)) return value;
  if (/^\/(?!\/)/.test(value)) return `${API_ORIGIN}${value}`;
  return `${API_ORIGIN}/${value.replace(/^\/+/, "")}`;
};

const normalizeName = (value: any) => {
  if (typeof value !== "string") return "";
  const raw = value.trim();
  if (!raw) return "";
  // Keep exact uploaded file name when backend sends it as metadata.
  if (!raw.includes("/") && !raw.includes("\\")) return raw;
  return raw.split("/").pop() || raw;
};

const extractFromUnknown = (source: any): { url: string; name: string } => {
  if (!source) return { url: "", name: "" };

  // Backend may return direct string path/url.
  if (typeof source === "string") {
    const clean = source.trim();
    if (isLikelyUrlOrPath(clean)) {
      return { url: toAbsoluteUrl(clean), name: normalizeName(clean) };
    }
    return { url: "", name: normalizeName(clean) };
  }

  if (typeof source !== "object") return { url: "", name: "" };

  const directUrl =
    source.url ||
    source.path ||
    source.src ||
    source.link ||
    "";
  const directNameRaw =
    source.originalName ||
    source.fileName ||
    source.filename ||
    source.name ||
    source.key ||
    "";
  const directName = normalizeName(directNameRaw);

  if (directUrl && isLikelyUrlOrPath(String(directUrl))) {
    const resolved = toAbsoluteUrl(String(directUrl));
    return { url: resolved, name: String(directName || normalizeName(resolved)) };
  }

  // Common nested wrappers used by different upload handlers.
  const nestedCandidates = [source.file, source.document, source.value, source.data];
  for (const candidate of nestedCandidates) {
    const nested = extractFromUnknown(candidate);
    if (nested.url) return nested;
  }

  // Last-resort: scan object values for first URL-like/string-like entry.
  for (const value of Object.values(source)) {
    const nested = extractFromUnknown(value);
    if (nested.url) return nested;
  }

  return { url: "", name: "" };
};

const DocumentRow: React.FC<{ label: string; doc?: FileDoc | null }> = ({ label, doc }) => {
  const extracted = extractFromUnknown(doc);
  const fileUrl = extracted.url;
  const fileName = extracted.name || labelize(label);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1.25,
        mb: 1,
        borderRadius: 1,
        background: "#F4F7FC",
      }}
    >
      <Typography fontWeight={500}>{labelize(label)}</Typography>
      {fileUrl ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography color="text.secondary" fontSize="0.9rem">
            {fileName}
          </Typography>
          <IconButton
            size="small"
            component="a"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${label}`}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            component="a"
            href={fileUrl}
            download={fileName}
            aria-label={`Download ${label}`}
          >
            <DownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Typography color="text.secondary" fontSize="0.9rem">
          Not uploaded
        </Typography>
      )}
    </Box>
  );
};

const Section: React.FC<{ title: string; rows: Array<{ label: string; doc?: any }> }> = ({
  title,
  rows,
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography fontWeight={700} sx={{ mb: 1 }}>
      {title}
    </Typography>
    {rows.length > 0 ? (
      rows.map((row) => <DocumentRow key={`${title}-${row.label}`} label={row.label} doc={row.doc} />)
    ) : (
      <Typography color="text.secondary" sx={{ py: 1 }}>
        No documents available
      </Typography>
    )}
  </Box>
);

const Documents: React.FC<DocumentsProps> = ({ data }) => {
  const personalRows = useMemo(
    () =>
      Object.entries(data?.personal ?? {}).map(([key, value]) => ({
        label: key,
        doc: value,
      })),
    [data?.personal]
  );

  const academicRows = useMemo(
    () =>
      Object.entries(data?.academic ?? {}).map(([key, value]) => ({
        label: key,
        doc: value,
      })),
    [data?.academic]
  );

  const testRows = useMemo(() => {
    if (Array.isArray(data?.test)) {
      return data!.test.map((doc, idx) => ({
        label: (doc as any)?.fileName || `Test Document ${idx + 1}`,
        doc,
      }));
    }
    return Object.entries(data?.test ?? {}).map(([key, value]) => ({
      label: key,
      doc: value as FileDoc | null,
    }));
  }, [data?.test]);

  const workRows = useMemo(
    () =>
      (data?.workExperiences ?? []).map((doc, idx) => ({
        label: doc?.fileName || `Work Experience ${idx + 1}`,
        doc,
      })),
    [data?.workExperiences]
  );

  return (
    <Box>
      <Typography sx={{ background: "#FCF1EE", width: "fit-content", p: 1, borderRadius: "10px", mb: 2 }}>
        Documents
      </Typography>
      <Section title="Personal Documents" rows={personalRows} />
      <Section title="Academic Documents" rows={academicRows} />
      <Section title="Aptitude Test Marksheet" rows={testRows} />
      <Section title="Work Experience" rows={workRows} />
    </Box>
  );
};

export default Documents;